import * as React from 'react'
import { connect } from 'react-redux'
import { TidewaysContext } from './app/context'
import Navigator from './app/navigator'
import { EventItem } from './domain/EventItem'
import { createHistoryCriteria, HistoryCriteria, historyCriteriaDescription, historyTrendCriteria } from './domain/history/HistoryCriteria'
import { AggregatedServiceReport } from './domain/history/ServiceReport'
import { TransactionTrendReport } from './domain/history/TransactionTrendReport'
import { Trends } from './domain/history/Trends'
import { Report } from './domain/Report'
import { SerializedHistogram } from './domain/SerializedHistogram'
import { ServiceType } from './domain/ServiceType'
import Loading from './helper/loading'
import { PortalContentNavigation } from './helper/portal-content-navigation'
import { ErrorSummary } from './history/error-summary'
import { GraphStats } from './history/graph-stats'
import { NoData } from './history/no-data'
import { rangeSelectorHistoryView } from './history/rangeSelectorHistoryView'
import { TooltipGraphComponent } from './history/tooltip-graph'
import TransactionReport from './history/transactionReport'
import TrendsComponent from './history/trends'
import { TimeExplorerComponent } from './monitor/history/time_explorer'
import ProfileTable from './monitor/profile_table'
import { ApplicationState } from './redux/ApplicationState'
import { fetchDailyReportsFor, fetchTimeExplorerReportFor } from './redux/V2/DailyReport/actions'
import {
    getAggregatedReportFor,
    getHistoryTransactionTrendReports,
    getHistoryTrendsFor,
    getTimeExplorerReportFor,
} from './redux/V2/DailyReport/DailyReportState'
import { fetchEventsForHistory } from './redux/V2/Event/actions'
import { getEventsForHistory } from './redux/V2/Event/EventState'
import { Box, BoxBody, BoxHeader, BoxTitle } from './ui/box'
import { Col, Row } from './ui/grid'
import { UpgradeNecessary } from './upgrade'

interface Props {
    navigator: any
    tidewaysContext: TidewaysContext
    criteria: HistoryCriteria
    fetchDailyReportsFor: (criteria: HistoryCriteria) => void
    fetchEventsForHistory: (criteria: HistoryCriteria) => void
    fetchTimeExplorerReportFor: (environment: string, service: string) => void
    reportMonitoring: AggregatedServiceReport | null
    reportTimeExplorer: Report<SerializedHistogram> | null
    reportTransactions: TransactionTrendReport[] | null
    trends: Trends | null
    events: EventItem[]
}

interface State {
    hideErrors: boolean
    view: 'transactions' | 'traces' | 'trends' | 'errors'
}

export class HistoryComponent extends React.PureComponent<Props, State> {
    public state: State = {
        view: 'trends',
        hideErrors: false,
    }
    public componentDidMount(): void {
        const serviceType = this.props.tidewaysContext.route.parameters.st

        // Temporarily hide external version of the history until we have a final variant
        if (serviceType === ServiceType.external) {
            this.props.navigator.replace({ ...this.props.tidewaysContext.route.parameters, st: ServiceType.php })

            return
        }

        if (['transactions', 'traces', 'trends', 'errors'].includes(this.props.tidewaysContext.route.parameters.view)) {
            this.setState({ view: this.props.tidewaysContext.route.parameters.view as any })
        }

        this.fetchData()
        this.fetchTimeExplorerData()
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        const granularityChanged = prevProps.criteria.granularity !== this.props.criteria.granularity
        const dateChanged = prevProps.criteria.date
            ? prevProps.criteria.date.format('YYYY-MM-DD') !== this.props.criteria.date.format('YYYY-MM-DD')
            : false

        if (granularityChanged || dateChanged) {
            this.fetchData()
        }

        const serviceChanged = prevProps.criteria.service !== this.props.criteria.service
        const serviceTypeChanged = prevProps.criteria.serviceType !== this.props.criteria.serviceType
        const environmentChanged = prevProps.criteria.environment !== this.props.criteria.environment

        if (serviceChanged || serviceTypeChanged || environmentChanged) {
            this.fetchData()
            this.fetchTimeExplorerData()
        }
    }

    public render() {
        const aggregatedReport = this.props.reportMonitoring
        const transactionReportAdditionalProps = this.props.tidewaysContext.application.license.historyTransactions
            ? {
                  route: 'XhprofProfilerBundle.History.operationHistory',
                  parameters: {
                      s: this.props.criteria.service,
                      st: this.props.criteria.serviceType,
                      env: this.props.criteria.environment,
                      date: this.props.criteria.date.format('YYYY-MM-DD'),
                      granularity: this.props.criteria.granularity,
                  },
              }
            : {}
        const archivedTracesSupported = this.props.tidewaysContext.application.license.historyTraces
        const hasArchivedTraces = this.props.reportMonitoring?.archivedTraces && this.props.reportMonitoring?.archivedTraces?.total > 0
        return (
            <Row>
                {this.renderTimeExplorer()}
                {!aggregatedReport ? (
                    <Loading />
                ) : (
                    <Col className="monitor history-box">
                        <Box>
                            <BoxHeader>
                                <nav className="navbar navbar-toolbar">
                                    <div className="container-fluid">
                                        <BoxTitle>
                                            History <strong>{historyCriteriaDescription(this.props.criteria)}</strong>
                                        </BoxTitle>
                                        <GraphStats
                                            report={aggregatedReport.report || null}
                                            hasCrawlerRatio={this.props.tidewaysContext.hasFeature('crawler_ratio')}
                                        />
                                    </div>
                                </nav>
                            </BoxHeader>
                            <BoxBody>
                                {aggregatedReport.noData ? (
                                    <NoData date={this.props.criteria.date} />
                                ) : (
                                    <TooltipGraphComponent
                                        {...this.props}
                                        data={{ report: aggregatedReport.report, events: this.props.events }}
                                        parameters={{}}
                                        dataFixedTimezone={this.props.tidewaysContext.application.timezone}
                                    />
                                )}
                            </BoxBody>
                        </Box>
                    </Col>
                )}
                {aggregatedReport && !aggregatedReport.noData ? (
                    <React.Fragment>
                        <div className="col-lg-12">
                            <ul className="nav nav-pills nav-pills-tideways">
                                <li className={this.state.view === 'trends' ? 'active' : ''}>
                                    <a
                                        onClick={() => {
                                            this.setState({ view: 'trends' })
                                        }}
                                    >
                                        Trends
                                    </a>
                                </li>
                                <li className={this.state.view === 'transactions' ? 'active' : ''}>
                                    <a
                                        onClick={() => {
                                            this.setState({ view: 'transactions' })
                                        }}
                                    >
                                        Transaction
                                    </a>
                                </li>

                                <li className={this.state.view === 'traces' ? 'active' : ''}>
                                    <a
                                        onClick={() => {
                                            this.setState({ view: 'traces' })
                                        }}
                                    >
                                        Traces
                                    </a>
                                </li>

                                <li className={this.state.view === 'errors' ? 'active' : ''}>
                                    <a
                                        onClick={() => {
                                            this.setState({ view: 'errors' })
                                        }}
                                    >
                                        Errors
                                    </a>
                                </li>
                            </ul>
                        </div>
                        {this.props.trends && this.state.view === 'trends' ? (
                            <div className="col-lg-12">
                                <div className="box box-default">
                                    <TrendsComponent data={{ trends: this.props.trends }} />
                                </div>
                            </div>
                        ) : null}
                        {this.props.reportTransactions && this.props.reportTransactions.length > 1 && this.state.view === 'transactions' ? (
                            <TransactionReport
                                collapsable
                                data={{ transactionReport: this.props.reportTransactions }}
                                tidewaysContext={this.props.tidewaysContext}
                                {...transactionReportAdditionalProps}
                            />
                        ) : null}
                        {this.state.view === 'traces' && !archivedTracesSupported ? (
                            <UpgradeNecessary title={''} featureName={'History traces'} />
                        ) : null}
                        {this.state.view === 'traces' && archivedTracesSupported && !hasArchivedTraces ? (
                            <Row>
                                <Col width={6} centered offsetMedium={3} widthSmall={12}>
                                    <Box>
                                        <BoxHeader>
                                            <BoxTitle>No archived traces found</BoxTitle>
                                        </BoxHeader>

                                        <BoxBody style={{ fontSize: '16px' }}>
                                            <p>There are no archived traces for the selected service and time-span.</p>
                                        </BoxBody>
                                    </Box>
                                </Col>
                            </Row>
                        ) : null}
                        {this.state.view === 'traces' &&
                        archivedTracesSupported &&
                        this.props.reportMonitoring?.archivedTraces &&
                        hasArchivedTraces ? (
                            <div className="col-md-12">
                                <div className="box">
                                    <div className="box-body no-padding">
                                        <ProfileTable
                                            profiles={this.props.reportMonitoring.archivedTraces}
                                            profileRoute="XhprofProfilerBundle.Profile.quick"
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        {!this.state.hideErrors && this.state.view === 'errors' ? (
                            <Col width={4}>
                                <ErrorSummary errors={aggregatedReport.errors || []} />
                            </Col>
                        ) : null}
                    </React.Fragment>
                ) : null}
            </Row>
        )
    }

    private fetchData = () => {
        this.props.fetchDailyReportsFor(this.props.criteria)
        this.props.fetchEventsForHistory(this.props.criteria)

        historyTrendCriteria(this.props.criteria).map(trendCriteria => this.props.fetchDailyReportsFor(trendCriteria))
    }

    private fetchTimeExplorerData = () => {
        const { service, environment } = this.props.tidewaysContext

        this.props.fetchTimeExplorerReportFor(environment, service)
    }

    private renderTimeExplorer = () => {
        if (!this.props.reportTimeExplorer) {
            return <Loading />
        }

        return (
            <PortalContentNavigation>
                <TimeExplorerComponent
                    data={this.props.reportTimeExplorer}
                    loading={false}
                    tidewaysContext={this.props.tidewaysContext}
                    navigator={this.props.navigator}
                    daysOnScale={21}
                    dataFixedTimezone={this.props.tidewaysContext.application.timezone}
                    selector={rangeSelectorHistoryView(
                        this.props.tidewaysContext,
                        this.props.navigator,
                        this.props.criteria.granularity,
                        this.props.criteria.date,
                    )}
                />
            </PortalContentNavigation>
        )
    }
}

export default connect(
    (globalState: ApplicationState, props: Props) => {
        const criteria = createHistoryCriteria(globalState.tidewaysContext)

        return {
            tidewaysContext: globalState.tidewaysContext,
            navigator: new Navigator(globalState.tidewaysContext.route),
            criteria,
            reportMonitoring: getAggregatedReportFor(globalState, criteria),
            reportTimeExplorer: getTimeExplorerReportFor(globalState, criteria.environment, criteria.service),
            trends: getHistoryTrendsFor(globalState, criteria),
            reportTransactions: getHistoryTransactionTrendReports(globalState, criteria),
            events: getEventsForHistory(globalState, criteria),
        }
    },
    {
        fetchDailyReportsFor,
        fetchTimeExplorerReportFor,
        fetchEventsForHistory,
    },
)(HistoryComponent)
